import type { ChestContextConfig, ChestItemConfig } from './chestConfigTypes';
import { defaultMerchWeight, MERCH_ITEMS } from './chestConfigTypes';

function isEquipmentKey(key: string): boolean {
    return /^(rod|reel|lure|bait|vessel|companion)_/.test(key);
}

export function sumEnabledWeights(ctx: ChestContextConfig): number {
    return Object.values(ctx.items).reduce(
        (sum, item) => sum + (item.enabled ? Number(item.weight) || 0 : 0),
        0
    );
}

export function updateItemWeight(
    ctx: ChestContextConfig,
    key: string,
    weight: number
): ChestContextConfig {
    const item = ctx.items[key];
    if (!item) return ctx;
    const w = Math.max(0, weight);
    return {
        ...ctx,
        items: {
            ...ctx.items,
            [key]: {
                ...item,
                weight: w,
                enabled: w > 0 ? true : item.enabled,
            },
        },
    };
}

export function toggleContextEnabled(ctx: ChestContextConfig, enabled: boolean): ChestContextConfig {
    return { ...ctx, enabled };
}

/** Al desactivar un ítem, redistribuye su peso entre los demás activos proporcionalmente. */
export function toggleItemEnabled(
    ctx: ChestContextConfig,
    key: string,
    enabled: boolean
): ChestContextConfig {
    const item = ctx.items[key];
    if (!item) return ctx;

    if (enabled) {
        const isMerch = MERCH_ITEMS.some((m) => m.key === key);
        const restoredWeight =
            Number(item.weight) > 0
                ? Number(item.weight)
                : isMerch
                  ? defaultMerchWeight(key)
                  : isEquipmentKey(key)
                    ? 0.1
                    : 1;
        return {
            ...ctx,
            items: {
                ...ctx.items,
                [key]: { ...item, enabled: true, weight: restoredWeight },
            },
        };
    }

    const disabledWeight = item.enabled ? Number(item.weight) || 0 : 0;
    const otherKeys = Object.keys(ctx.items).filter((k) => k !== key && ctx.items[k]?.enabled);
    const otherTotal = otherKeys.reduce((s, k) => s + (Number(ctx.items[k]?.weight) || 0), 0);

    const nextItems: Record<string, ChestItemConfig> = {
        ...ctx.items,
        [key]: { ...item, enabled: false, weight: 0 },
    };

    if (disabledWeight > 0 && otherKeys.length > 0 && otherTotal > 0) {
        for (const k of otherKeys) {
            const w = Number(nextItems[k].weight) || 0;
            const share = (w / otherTotal) * disabledWeight;
            nextItems[k] = { ...nextItems[k], weight: Math.round((w + share) * 100) / 100 };
        }
    }

    return { ...ctx, items: nextItems };
}

export function groupItemsByCategory<T extends { group: string }>(items: T[]): Map<string, T[]> {
    const map = new Map<string, T[]>();
    for (const item of items) {
        const list = map.get(item.group) ?? [];
        list.push(item);
        map.set(item.group, list);
    }
    return map;
}
